import { useIntl } from "react-intl";

import { useGetInvoiceAttachmentBuckets } from "api/queries/invoice/useGetInvoiceAttachmentBuckets";
import { useGetPodBucketAttachments } from "api/queries/shipments/useGetPodBucketAttachments";
import { useGetPODAttachmentBuckets } from "api/queries/shipments/useGetPODAttachmentBuckets";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { OTHER_BUCKET } from "components/molecules/TopNav/common/NavIcons/PickupDeliveryProofDialog/pickupDeliveryProofOptions";
import {
  attachmentMatchesBucket,
  isOngoingPodAttachmentReadOnly,
} from "components/molecules/TopNav/common/NavIcons/PickupDeliveryProofDialog/podBucketAttachmentHelpers";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import Card from "components/v2/Card/Card";
import { ShipmentTypes } from "constants/";

import PodAttachmentRow from "./PodAttachmentRow";
import { shouldShowPodAttachmentsCard } from "./shouldShowPodAttachmentsCard";

import "./PodAttachmentsCard.scss";

const attachmentBucketSelector = (data: { results?: unknown[] }) =>
  !data?.results?.length ? OTHER_BUCKET : data.results;

type PodAttachmentsCardProps = {
  shipmentId: string;
  shipmentDetails: {
    settings?: {
      pod_attachment_buckets?: boolean;
      invoice_attachment_buckets?: boolean;
      pre_delivery_pod_attachments_enabled?: boolean;
    };
    organization?: {
      shipment_status?: string | null;
    };
    is_multi_stop_shipment?: boolean;
  };
  isQuickRequest: boolean;
  isGuestUser: boolean;
  isCustomBroker: boolean;
  isLoading: boolean;
};

export default function PodAttachmentsCard({
  shipmentId,
  shipmentDetails,
  isQuickRequest,
  isGuestUser,
  isCustomBroker,
  isLoading,
}: PodAttachmentsCardProps) {
  const intl = useIntl();

  const { data: shipmentBasicData } = useGetShipmentBasic(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });

  const shipmentStatus = shipmentDetails?.organization?.shipment_status;
  const normalizedShipmentStatus = shipmentStatus?.toLowerCase() ?? "";
  const isMultistop =
    Boolean(shipmentDetails?.is_multi_stop_shipment) ||
    shipmentBasicData?.shipment?.shipment_type === ShipmentTypes.MULTISTOP;
  const isBooked = Boolean(shipmentStatus);
  const isCanceled = normalizedShipmentStatus === "canceled";
  const isReadOnly = isOngoingPodAttachmentReadOnly(shipmentStatus);

  const showCard = shouldShowPodAttachmentsCard({
    settingEnabled: Boolean(shipmentDetails?.settings?.pre_delivery_pod_attachments_enabled),
    isGuest: isGuestUser,
    isCanceled,
    isQuickRequest,
    isCustomBroker,
    isMultistop,
    isBooked,
  });

  const usePodBuckets = Boolean(shipmentDetails?.settings?.pod_attachment_buckets);
  const useInvoiceBuckets = Boolean(shipmentDetails?.settings?.invoice_attachment_buckets);

  const { data: podAttachmentBuckets = [], isInitialLoading: isPodInitialLoading } = useGetPODAttachmentBuckets({
    filters: { shipment: shipmentId },
    options: {
      enabled: showCard && !!shipmentId && usePodBuckets,
      select: attachmentBucketSelector,
    },
  });

  const { data: invoiceAttachmentBuckets = [], isInitialLoading: isInvoiceInitialLoading } =
    useGetInvoiceAttachmentBuckets({
      filters: { shipment: shipmentId },
      options: {
        enabled: showCard && !!shipmentId && useInvoiceBuckets,
        select: attachmentBucketSelector,
      },
    });

  const attachmentBuckets = (usePodBuckets
    ? podAttachmentBuckets
    : useInvoiceBuckets
      ? invoiceAttachmentBuckets
      : OTHER_BUCKET) as Array<{ id: number; bucket_name: string; is_mandatory?: boolean; isSyntheticOther?: boolean }>;

  const dataSource = usePodBuckets ? "pod" : "invoice";

  const bucketsLoading =
    isLoading || (usePodBuckets ? isPodInitialLoading : useInvoiceBuckets ? isInvoiceInitialLoading : false);

  const { data: podBucketAttachmentsData } = useGetPodBucketAttachments(shipmentId, {
    enabled: showCard && !!shipmentId,
  });

  const attachments = podBucketAttachmentsData?.results ?? [];
  const attachmentCount = attachments.length;

  const title = intl.formatMessage({
    id: "shipmentTracking.attachments.title",
    defaultMessage: "Attachments",
  });
  const titleWithCount = attachmentCount > 0 ? `${title} (${attachmentCount})` : title;
  const subtitle = intl.formatMessage({
    id: "shipmentTracking.attachments.subtitle",
    defaultMessage: "Submit POD to complete the shipment",
  });

  if (!showCard) {
    return null;
  }

  const findAttachmentForBucket = (bucket: { id: number; bucket_name: string; isSyntheticOther?: boolean }) =>
    attachments.find((item) => attachmentMatchesBucket(item, bucket));

  const visibleBuckets = isReadOnly
    ? attachmentBuckets.filter((bucket) => findAttachmentForBucket(bucket))
    : attachmentBuckets;

  return (
    <div className="pod-attachments-card mb-3xl">
      <Card borderRadius="md">
        <Card.Header title={titleWithCount} supportingText={subtitle} showBorder />
        <Card.Body>
          {bucketsLoading && <LoadingSkeleton rows={2} />}
          {!bucketsLoading &&
            visibleBuckets.map((bucket) => (
              <PodAttachmentRow
                key={bucket.id}
                bucket={bucket}
                dataSource={dataSource}
                attachment={findAttachmentForBucket(bucket)}
                shipmentId={shipmentId}
                isReadOnly={isReadOnly}
              />
            ))}
        </Card.Body>
      </Card>
    </div>
  );
}
